/* eslint-disable no-console */
import { useCallback, useState } from 'react';
import { getCurrentUser, fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { extractUserInfo } from 'src/utils/authUtils';
import { configureAgGridLicense } from 'src/utils/agGridLicenseConfig';
import { configureKatalLogger, entryLog } from 'src/utils/logger';
import { configureAmplifyAPIs } from '../config/amplifyConfig';
import type { NotificationItem, LoggedInUserInfo } from '../types/auth/auth.types';

const USER_AUTH_DETAILS_KEY = 'userAuthDetails';

export const useAuthState = () => {
  const [user, setUser] = useState<LoggedInUserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [isServicesInitialized, setIsServicesInitialized] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const getUserDetailsWithOverride = useCallback((defaultUserInfo: LoggedInUserInfo): LoggedInUserInfo => {
    // No longer using override logic - permissions come from Redux
    // Keep function for backward compatibility but just return default
    return defaultUserInfo;
  }, []);

  const checkAuthState = useCallback(async (): Promise<void> => {
    console.log('checkAuthState called', { isAuthInitialized, user: !!user });

    if (isAuthInitialized && user) return;

    setSessionExpired(false);
    try {
      setIsLoading(true);
      setError(null);

      console.log('Attempting to get current user...');
      const authUser = await getCurrentUser();
      const defaultUserInfo = await extractUserInfo(authUser);
      const userInfo = getUserDetailsWithOverride(defaultUserInfo);

      console.log('User authenticated:', userInfo.userAlias);

      if (userInfo.userAlias && !isAuthInitialized) {
        console.log('Initializing services...');
        configureKatalLogger(userInfo.userAlias, userInfo.displayName);
        await entryLog();
        await configureAgGridLicense();
        await configureAmplifyAPIs();

        setIsServicesInitialized(true);
        console.log('Services initialized');
      }

      setUser(userInfo);
      setIsAuthInitialized(true);
      setIsLoading(false);
    } catch (error) {
      console.log('No authenticated user found:', error);

      // Check if this is an Amplify configuration error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Auth UserPool not configured')) {
        console.log('Amplify configuration issue detected, will reconfigure on next auth attempt');
      }

      // User is not authenticated - set states but don't redirect here
      // The redirect will be handled in the AuthProvider component
      setUser(null);
      setError(null);
      setIsLoading(false);
      setIsAuthInitialized(true);
      // Mark services as initialized even without user to prevent infinite loading
      setIsServicesInitialized(true);
    }
  }, [isAuthInitialized, user, getUserDetailsWithOverride]);

  const updateUserAuthDetails = useCallback((updates: Partial<LoggedInUserInfo>, _fromModifyAuthContext: boolean) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      const updatedUser = { ...prevUser, ...updates };
      // No longer setting isUserRoleModified - permissions managed via Redux
      sessionStorage.setItem(USER_AUTH_DETAILS_KEY, JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  const resetToDefaultAuthDetails = useCallback(() => {
    sessionStorage.removeItem(USER_AUTH_DETAILS_KEY);
    void checkAuthState();
  }, [checkAuthState]);

  const signIn = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setSessionExpired(false);
      await signInWithRedirect({ provider: { custom: 'AmazonFederate' } });
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setSessionExpired(false);
      await amplifySignOut({ global: true });
      setUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign out failed';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setSessionExpired(false);
      await fetchAuthSession({ forceRefresh: true });
      await checkAuthState();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Session refresh failed';
      setError(errorMessage);
      setSessionExpired(true);
      throw err;
    }
  }, [checkAuthState]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  return {
    user,
    error,
    isLoading,
    isAuthInitialized,
    isServicesInitialized,
    sessionExpired,
    notifications,
    checkAuthState,
    updateUserAuthDetails,
    resetToDefaultAuthDetails,
    signIn,
    signOut,
    refreshSession,
    dismissNotification,
    setNotifications,
    setSessionExpired,
    setUser,
    setError,
    setIsLoading,
    setIsAuthInitialized,
  };
};
